// ATSCheckPage.js - Browser Compatible with PDF and DOCX parsing
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyzeATS } from '../services/openaiService';
import Header from './Header';
import Footer from './Footer';
import './ATSCheckPage.css';
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';
import ProductSidebar from './ProductSidebar';
import { useUser } from '../contexts/UserContext';

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const ATSCheckPage = ({ embedded = false } = {}) => {
  const navigate = useNavigate();
  const { user } = useUser();
  
  const [jobDescription, setJobDescription] = useState('');
  const [resumeText, setResumeText] = useState('');
  const [uploadedResumeFile, setUploadedResumeFile] = useState(null);
  const [uploadedJobDescFile, setUploadedJobDescFile] = useState(null);
  const [atsResult, setAtsResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [error, setError] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [resumeChoice, setResumeChoice] = useState(null); // 'profile' | 'upload'
  const resumeFileInputRef = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

  useEffect(() => {
    if (user) {
      fetch(`${API_BASE_URL}/api/profile`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data?.profile) setProfileData(data.profile); })
        .catch(() => {});
    }
  }, [user, API_BASE_URL]);

  const buildResumeTextFromProfile = (profile) => {
    let text = '';
    if (profile.fullName) text += `${profile.fullName}\n`;
    if (profile.jobTitle) text += `${profile.jobTitle}\n`;
    if (profile.phone) text += `Phone: ${profile.phone}\n`;
    if (profile.location) text += `Location: ${profile.location}\n`;
    if (profile.linkedIn) text += `LinkedIn: ${profile.linkedIn}\n`;
    if (profile.github) text += `GitHub: ${profile.github}\n`;
    text += '\n';
    if (profile.professionalSummary) text += `PROFESSIONAL SUMMARY\n${profile.professionalSummary}\n\n`;
    if (profile.skills) text += `SKILLS\n${profile.skills}\n\n`;
    if (Array.isArray(profile.workExperience) && profile.workExperience.some(e => e.company || e.position)) {
      text += 'WORK EXPERIENCE\n';
      profile.workExperience.forEach(exp => {
        if (exp.position || exp.company) {
          text += `${exp.position || ''}${exp.company ? ` at ${exp.company}` : ''}${exp.duration ? ` (${exp.duration})` : ''}\n`;
          if (exp.description) text += `${exp.description}\n`;
          text += '\n';
        }
      });
    }
    if (Array.isArray(profile.education) && profile.education.some(e => e.degree || e.institution)) {
      text += 'EDUCATION\n';
      profile.education.forEach(edu => {
        if (edu.degree || edu.institution) {
          text += `${edu.degree || ''}${edu.field ? ` in ${edu.field}` : ''}${edu.institution ? ` - ${edu.institution}` : ''}${edu.year ? ` (${edu.year})` : ''}\n`;
        }
      });
      text += '\n';
    }
    if (Array.isArray(profile.projects) && profile.projects.some(p => p.name)) {
      text += 'PROJECTS\n';
      profile.projects.forEach(proj => {
        if (proj.name) {
          text += `${proj.name}${proj.technologies ? ` (${proj.technologies})` : ''}\n`;
          if (proj.description) text += `${proj.description}\n`;
          text += '\n';
        }
      });
    }
    if (profile.certifications) text += `CERTIFICATIONS\n${profile.certifications}\n`;
    return text.trim();
  };

  const handleNavigateToHome = () => {
    navigate('/', { replace: true });
  };

  const handleNavigateToResumeTailor = () => {
    navigate('/resume-builder');
  };

  const handleJobDescriptionChange = (e) => {
    setJobDescription(e.target.value);
    setError('');
  };

  const handleResumeChange = (e) => {
    setResumeText(e.target.value);
    setError('');
  };

  // Function to extract text from PDF using pdfjs-dist
  const extractTextFromPDF = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = '';

      // Extract text from each page
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        fullText += pageText + '\n';
      }

      return fullText.trim();
    } catch (error) {
      console.error('Error parsing PDF:', error);
      throw new Error('Failed to parse PDF file. Please ensure the PDF contains readable text.');
    }
  };

  // Function to extract text from DOCX
  const extractTextFromDOCX = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      return result.value;
    } catch (error) {
      console.error('Error parsing DOCX:', error);
      throw new Error('Failed to parse DOCX file. Please try pasting the text manually.');
    }
  };

  // Function to extract text from DOC (older format)
  const extractTextFromDOC = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      return result.value;
    } catch (error) {
      console.error('Error parsing DOC:', error);
      throw new Error('Failed to parse DOC file. This format may not be fully supported. Please try converting to DOCX or pasting the text manually.');
    }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsProcessingFile(true);
    setError('');

    try {
      if (type === 'resume') {
        setUploadedResumeFile(file);
      } else {
        setUploadedJobDescFile(file);
      }

      let extractedText = '';

      // Handle different file types
      if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
        // Text files
        const reader = new FileReader();
        extractedText = await new Promise((resolve, reject) => {
          reader.onload = (event) => resolve(event.target.result);
          reader.onerror = (error) => reject(error);
          reader.readAsText(file);
        });
      } else if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        // PDF files
        extractedText = await extractTextFromPDF(file);
      } else if (
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.name.endsWith('.docx')
      ) {
        // DOCX files
        extractedText = await extractTextFromDOCX(file);
      } else if (file.type === 'application/msword' || file.name.endsWith('.doc')) {
        // DOC files (older format)
        extractedText = await extractTextFromDOC(file);
      } else {
        throw new Error('Unsupported file format. Please upload .txt, .pdf, .doc, or .docx files.');
      }

      // Clean up the extracted text
      extractedText = extractedText.trim();

      if (!extractedText) {
        throw new Error('No text could be extracted from the file. Please ensure the file contains readable text.');
      }

      // Set the extracted text to the appropriate field
      if (type === 'resume') {
        setResumeText(extractedText);
      } else {
        setJobDescription(extractedText);
      }

      // Clear success message after 3 seconds
      setTimeout(() => {
        setError('');
      }, 3000);

    } catch (error) {
      console.error('Error processing file:', error);
      setError(error.message || 'Failed to process file. Please try pasting the text manually.');
      
      // Clear the file input if there was an error
      if (type === 'resume') {
        setUploadedResumeFile(null);
      } else {
        setUploadedJobDescFile(null);
      }
    } finally {
      setIsProcessingFile(false);
    }
  };

  const handleAnalyzeATS = async () => {
    if (!jobDescription.trim()) {
      setError('Please enter a job description first');
      return;
    }

    if (!resumeText.trim()) {
      setError('Please enter or upload your resume');
      return;
    }

    setIsAnalyzing(true);
    setError('');

    try {
      const result = await analyzeATS(resumeText, jobDescription);
      setAtsResult(result);
    } catch (error) {
      console.error('Error analyzing ATS:', error);
      setError('Failed to analyze ATS compatibility. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleClearAll = () => {
    setJobDescription('');
    setResumeText('');
    setUploadedResumeFile(null);
    setUploadedJobDescFile(null);
    setAtsResult(null);
    setError('');
    setResumeChoice(null);
  };

  // Function to render score with color coding
  const renderScore = (score, max = 100) => {
    let color;
    if (score >= 80) color = '#4caf50';
    else if (score >= 60) color = '#ff9800';
    else color = '#f44336';
    
    return (
      <div className="score-display">
        <div className="score-circle" style={{ borderColor: color }}>
          <span style={{ color }}>{score}%</span>
        </div>
      </div>
    );
  };

  // Function to render checklist items
  const renderChecklistItem = (label, isPresent, recommendation) => (
    <div className="checklist-item">
      <div className="checklist-status">
        {isPresent ? (
          <span className="checkmark">✓</span>
        ) : (
          <span className="cross">✗</span>
        )}
      </div>
      <div className="checklist-content">
        <span className={isPresent ? 'present' : 'missing'}>{label}</span>
        {!isPresent && recommendation && (
          <div className="recommendation">{recommendation}</div>
        )}
      </div>
    </div>
  );

  return (
    <div className={embedded ? 'ats-embedded-root' : 'neural-glass-homepage'}>
      {!embedded && <div className="neural-background"></div>}

      {!embedded && (
        <div className="geometric-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
        </div>
      )}

      <div
        className={`ats-check-page-container ${embedded ? 'ats-check-embedded' : ''}`}
        style={embedded ? undefined : { '--page-hero-img': "url('/images/page-heroes/ats-check.png')" }}
      >
        {!embedded && <Header setShowAuthModal={setShowAuthModal} />}

        {!embedded && <ProductSidebar user={user} setShowAuthModal={setShowAuthModal} />}

        <main className="ats-check-page-main">
          {!embedded && (
            <div className="li-hero">
              <div className="li-hero-badge">🚀 AI-Powered</div>
              <h1 className="li-hero-title">
                ATS <span className="li-gradient-text">Resume Checker</span>
              </h1>
              <p className="li-hero-subtitle">
                Analyze your resume with an AI ATS checker to beat resume filters and stand out to recruiters.
              </p>
            </div>
          )}
          <div className="ats-check-main-container">
            <div className="ats-check-content">
              <div className="input-panel">
                <div className="job-description-section">
                  <div className="job-description-container glass">
                    <div className="section-header">
                      <h3>Job Description</h3>
                      <div className="file-upload-wrapper">
                        <label htmlFor="job-desc-upload" className="file-upload-label">
                          {isProcessingFile ? 'Processing...' : 'Upload File'}
                        </label>
                          <input
                            id="job-desc-upload"
                            type="file"
                            accept=".txt,.doc,.docx,.pdf"
                            onChange={(e) => handleFileUpload(e, 'jobDesc')}
                            disabled={isAnalyzing || isProcessingFile}
                            style={{ display: 'none' }}
                          />
                      </div>
                    </div>
                    <textarea
                      value={jobDescription}
                      onChange={handleJobDescriptionChange}
                      placeholder="Paste the job description here or upload a file (PDF, DOCX, DOC, TXT)..."
                      rows="10"
                      disabled={isAnalyzing || isProcessingFile}
                    ></textarea>
                    {uploadedJobDescFile && (
                      <div className="file-info">
                        📄 {uploadedJobDescFile.name}
                      </div>
                    )}
                  </div>
                </div>

                <div className="resume-input-section">
                  <div className="resume-input-container glass">
                    <div className="section-header">
                      <h3>Your Resume</h3>
                      {(!profileData || !(profileData.professionalSummary || profileData.skills || (Array.isArray(profileData.workExperience) && profileData.workExperience.some(e => e.company || e.position)))) && (
                        <div className="file-upload-wrapper">
                          <label htmlFor="resume-upload" className="file-upload-label">
                            {isProcessingFile ? 'Processing...' : 'Upload Resume'}
                          </label>
                          <input
                            id="resume-upload"
                            type="file"
                            accept=".txt,.doc,.docx,.pdf"
                            onChange={(e) => handleFileUpload(e, 'resume')}
                            disabled={isAnalyzing || isProcessingFile}
                            style={{ display: 'none' }}
                          />
                        </div>
                      )}
                    </div>

                    {profileData && (profileData.professionalSummary || profileData.skills || (Array.isArray(profileData.workExperience) && profileData.workExperience.some(e => e.company || e.position))) ? (
                      <>
                        <div className="resume-choice-buttons-ats">
                          <button
                            type="button"
                            onClick={() => {
                              setResumeChoice('profile');
                              setResumeText(buildResumeTextFromProfile(profileData));
                              setUploadedResumeFile(null);
                              setError('');
                            }}
                            className={`resume-choice-btn-ats${resumeChoice === 'profile' ? ' resume-choice-btn-ats-active' : ''}`}
                            disabled={isAnalyzing || isProcessingFile}
                          >
                            <div className="resume-choice-label-ats">✅ Use Profile Resume</div>
                            {profileData.fullName && <div className="resume-choice-sublabel-ats">{profileData.fullName}</div>}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setResumeChoice('upload');
                              setResumeText('');
                              setUploadedResumeFile(null);
                            }}
                            className={`resume-choice-btn-ats${resumeChoice === 'upload' ? ' resume-choice-btn-ats-active' : ''}`}
                            disabled={isAnalyzing || isProcessingFile}
                          >
                            <div className="resume-choice-label-ats">📄 Upload New Resume</div>
                            <div className="resume-choice-sublabel-ats">Choose a different file</div>
                          </button>
                        </div>

                        {resumeChoice === 'profile' && (
                          <div className="profile-resume-confirm-ats">
                            ✓ Profile resume loaded. You can edit below if needed.
                          </div>
                        )}

                        {resumeChoice === 'upload' && (
                          <div className="file-upload-wrapper" style={{ marginBottom: '10px' }}>
                            <input
                              ref={resumeFileInputRef}
                              type="file"
                              accept=".txt,.doc,.docx,.pdf"
                              onChange={(e) => handleFileUpload(e, 'resume')}
                              disabled={isAnalyzing || isProcessingFile}
                              style={{ display: 'none' }}
                            />
                            <label
                              className="file-upload-label"
                              onClick={() => resumeFileInputRef.current?.click()}
                              style={{ cursor: 'pointer' }}
                            >
                              {isProcessingFile ? 'Processing...' : uploadedResumeFile ? `✓ ${uploadedResumeFile.name}` : 'Upload Resume (PDF/DOCX/TXT)'}
                            </label>
                          </div>
                        )}

                        {!resumeChoice && (
                          <p className="upload-hint-ats">Select an option above to provide your resume</p>
                        )}
                      </>
                    ) : null}

                    <textarea
                      value={resumeText}
                      onChange={handleResumeChange}
                      placeholder="Paste your resume content here or upload a file (PDF, DOCX, DOC, TXT)..."
                      rows="15"
                      disabled={isAnalyzing || isProcessingFile}
                    ></textarea>
                    {uploadedResumeFile && (
                      <div className="file-info">
                      </div>
                    )}
                    <div className="section-footer">
                      <button 
                        onClick={handleAnalyzeATS} 
                        className="analyze-btn"
                        disabled={isAnalyzing || isProcessingFile || !resumeText.trim() || !jobDescription.trim()}
                      >
                        {isAnalyzing ? (
                          <span className="loading">
                            <span className="typing-indicator">
                              <span></span>
                              <span></span>
                              <span></span>
                            </span>
                            Analyzing...
                          </span>
                        ) : (
                          'Analyze ATS Compatibility'
                        )}
                      </button>
                      <button 
                        onClick={handleClearAll} 
                        className="clear-button"
                        disabled={isAnalyzing || isProcessingFile}
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="results-panel">
                <div className="results-container glass">
                  <div className="section-header">
                    <h3>ATS Analysis Results</h3>
                  </div>
                  <div className="results-content">
                    {isProcessingFile ? (
                      <div className="loading-placeholder">
                        <span className="typing-indicator">
                          <span></span>
                          <span></span>
                          <span></span>
                        </span>
                        Processing uploaded file...
                      </div>
                    ) : isAnalyzing ? (
                      <div className="loading-placeholder">
                        <span className="typing-indicator">
                          <span></span>
                          <span></span>
                          <span></span>
                        </span>
                        Analyzing your resume against the job description...
                      </div>
                    ) : atsResult ? (
                      <div className="ats-results">
                        <div className="score-section">
                          <h4>Overall Match Score</h4>
                          {renderScore(atsResult.overallScore)}
                          <p className="score-description">
                            {atsResult.overallScore >= 80 
                              ? 'Excellent match! Your resume is well-aligned with the job requirements.'
                              : atsResult.overallScore >= 60
                              ? 'Good match, but there is room for improvement.'
                              : 'Your resume needs significant improvements to match the job requirements.'
                            }
                          </p>
                        </div>

                        <div className="keyword-section">
                          <h4>Keyword Analysis</h4>
                          <div className="keyword-match">
                            <span className="keyword-label">Keywords matched:</span>
                            <span className="keyword-value">
                              {atsResult.keywordMatches.length} of {atsResult.totalKeywords} keywords
                            </span>
                          </div>
                          <div className="keyword-list">
                            <h5>Missing Keywords:</h5>
                            <div className="keyword-tags">
                              {atsResult.missingKeywords.map((keyword, index) => (
                                <span key={index} className="keyword-tag missing">{keyword}</span>
                              ))}
                            </div>
                            <h5>Matched Keywords:</h5>
                            <div className="keyword-tags">
                              {atsResult.keywordMatches.map((keyword, index) => (
                                <span key={index} className="keyword-tag matched">{keyword}</span>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="checklist-section">
                          <h4>Resume Completeness</h4>
                          <div className="checklist">
                            {renderChecklistItem(
                              "Contact Information", 
                              atsResult.checklist.contactInfo, 
                              "Include your phone number, email, and location"
                            )}
                            {renderChecklistItem(
                              "Education Section", 
                              atsResult.checklist.education, 
                              "Add your educational background with degrees and institutions"
                            )}
                            {renderChecklistItem(
                              "Work Experience", 
                              atsResult.checklist.experience, 
                              "Detail your professional experience with accomplishments"
                            )}
                            {renderChecklistItem(
                              "Skills Section", 
                              atsResult.checklist.skills, 
                              "Include a dedicated skills section relevant to the job"
                            )}
                            {renderChecklistItem(
                              "Projects", 
                              atsResult.checklist.projects, 
                              "Add relevant projects to demonstrate practical experience"
                            )}
                            {renderChecklistItem(
                              "Certifications", 
                              atsResult.checklist.certifications, 
                              "List any relevant certifications you have obtained"
                            )}
                          </div>
                        </div>

                        <div className="format-section">
                          <h4>Format Analysis</h4>
                          <div className="format-score">
                            {renderScore(atsResult.formatScore * 10, 100)}
                            <span className="format-label">ATS Readability</span>
                          </div>
                          <div className="format-recommendations">
                            <h5>Format Recommendations:</h5>
                            <ul>
                              {atsResult.formatRecommendations.map((rec, index) => (
                                <li key={index}>{rec}</li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        <div className="actions-section">
                          <h4>Next Steps</h4>
                          <div className="action-buttons">
                            <button 
                              onClick={handleNavigateToResumeTailor} 
                              className="action-button primary"
                            >
                              Tailor This Resume
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="placeholder">
                        Upload your resume and job description files (PDF, DOCX, DOC, or TXT), or paste the text directly. Then click "Analyze ATS Compatibility" to get your detailed report.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <div className={`error-message glass ${error.startsWith('✓') ? 'success-message' : ''}`}>
                {error}
              </div>
            )}
          </div>
        </main>

        {!embedded && (
        <>
        <section className="info-section glass">
          <h2 className="section-title">Why Use ATS Resume Checker?</h2>
          <div className="info-grid">
            <div className="info-card glass">
              <div className="info-icon">🎯</div>
              <h3>Beat the Bots</h3>
              <p>Over 90% of large companies use ATS software to screen resumes. Our tool helps ensure your resume makes it past these automated systems to reach human recruiters.</p>
            </div>
            <div className="info-card glass">
              <div className="info-icon">🔍</div>
              <h3>Keyword Optimization</h3>
              <p>Identify missing keywords from the job description and optimize your resume to match what recruiters are looking for without keyword stuffing.</p>
            </div>
            <div className="info-card glass">
              <div className="info-icon">📊</div>
              <h3>Instant Feedback</h3>
              <p>Get detailed analysis of your resume's ATS compatibility in seconds, with actionable recommendations to improve your match score.</p>
            </div>
          </div>
        </section>

        <section className="how-it-works-section glass">
          <h2 className="section-title">How It Works</h2>
          <div className="steps-grid">
            <div className="step-card glass">
              <div className="step-number">1</div>
              <h4>Upload Job Description</h4>
              <p>Copy and paste the job posting you're applying for, or upload the job description file (PDF, DOCX, DOC, TXT).</p>
            </div>
            <div className="step-card glass">
              <div className="step-number">2</div>
              <h4>Add Your Resume</h4>
              <p>Paste your resume content or upload your resume file. Our system automatically extracts text from PDF and DOCX formats.</p>
            </div>
            <div className="step-card glass">
              <div className="step-number">3</div>
              <h4>Analyze & Review</h4>
              <p>Click analyze to get a comprehensive ATS compatibility report with keyword matching and formatting feedback.</p>
            </div>
            <div className="step-card glass">
              <div className="step-number">4</div>
              <h4>Optimize & Apply</h4>
              <p>Use our recommendations to improve your resume, or use the tailor feature to automatically optimize it for the job.</p>
            </div>
          </div>
        </section>

        <section className="tips-section glass">
          <h2 className="section-title">ATS Optimization Tips</h2>
          <div className="tips-columns">
            <div className="tip-column glass">
              <h4>Formatting Best Practices</h4>
              <ul>
                <li>Use standard section headings like "Work Experience" and "Education"</li>
                <li>Avoid tables, text boxes, headers, and footers</li>
                <li>Use standard fonts like Arial, Calibri, or Times New Roman</li>
                <li>Save as .docx or .pdf format (check job posting preferences)</li>
                <li>Keep formatting simple with clear section breaks</li>
                <li>Use bullet points for achievements, not paragraphs</li>
              </ul>
            </div>
            <div className="tip-column glass">
              <h4>Keyword Strategy</h4>
              <ul>
                <li>Mirror language from the job description naturally</li>
                <li>Include both acronyms and full terms (e.g., "SEO" and "Search Engine Optimization")</li>
                <li>Use industry-specific terminology and technical skills</li>
                <li>Place important keywords in multiple sections</li>
                <li>Avoid keyword stuffing - maintain natural flow</li>
                <li>Include both hard and soft skills mentioned in posting</li>
              </ul>
            </div>
            <div className="tip-column glass">
              <h4>Content Guidelines</h4>
              <ul>
                <li>Quantify achievements with numbers and percentages</li>
                <li>Start bullet points with strong action verbs</li>
                <li>Tailor your resume for each job application</li>
                <li>Include relevant certifications and licenses</li>
                <li>Keep resume to 1-2 pages maximum</li>
                <li>Proofread for spelling and grammar errors</li>
              </ul>
            </div>
          </div>
        </section>
        </>
        )}

        {!embedded && <Footer />}
      </div>
    </div>
  );
};

export default ATSCheckPage;